import { TYPES, container } from '@/libs/di/inversify.config'
import type { MessageUsecase } from '@/apis/usecases'
import type { Normalized } from '@/libs/utilities/normalize'
import {
  createNormalizedEmpty,
  denormalize,
  normalize,
} from '@/libs/utilities/normalize'
import type { AnyMessage, Pagination, RoomID } from '@/types/app'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'

import { isClientError } from '@/libs/utilities/CustomErrors'
import { captureException as newrelicCaptureException } from '@/newrelic'

const NUMBER_OF_MESSAGES_PER_FETCH = 20

type NormalizedMessages = Normalized<AnyMessage, 'id'>

type Message = {
  store: NormalizedMessages
  hasPrevPage: boolean
  hasNextPage: boolean
}

const getInitialMessageState = (): Message => ({
  store: createNormalizedEmpty<AnyMessage, 'id'>(),
  hasNextPage: true,
  hasPrevPage: true,
})

const normalizeAndFilterMessages = (
  currentStore: NormalizedMessages,
  messages: readonly AnyMessage[]
): NormalizedMessages => {
  const { entities, values } = normalize(messages, 'id')
  const filteredValues = values.filter(id => !currentStore.entities.has(id))
  return { entities, values: filteredValues }
}

export const useFetchMessages = (
  roomId: Ref<Maybe<RoomID>>,
  isDisabledToScrollToBottom: Ref<boolean>,
  entranceGUID: string
) => {
  const message = ref<Message>(getInitialMessageState())

  const messages = computed((): ReadonlyArray<AnyMessage> => {
    return denormalize<AnyMessage, 'id'>(
      message.value.store.values,
      message.value.store.entities
    )
  })

  const nextMessagesRequestParameters = computed((): Pagination => {
    const { values, entities } = message.value.store

    if (values.length === 0) {
      return { limit: NUMBER_OF_MESSAGES_PER_FETCH }
    }

    const lastId = values[values.length - 1]
    const since = entities.get(lastId)?.createdAt ?? undefined

    return {
      since,
      limit: NUMBER_OF_MESSAGES_PER_FETCH,
    }
  })

  const loading = ref(false)

  const fetchNextMessages = async (forceFetch = false) => {
    if (!roomId.value || (!forceFetch && !message.value.hasNextPage)) {
      return
    }

    loading.value = true

    try {
      const messageUsecase = container.get<MessageUsecase>(TYPES.MessageUsecase)
      const {
        data,
        pageInfo: { hasNextPage },
      } = await messageUsecase.getMemberRoomMessages(
        roomId.value,
        entranceGUID,
        nextMessagesRequestParameters.value
      )

      const { values, entities } = normalizeAndFilterMessages(
        message.value.store,
        data
      )

      message.value.store.entities = new Map([
        ...message.value.store.entities,
        ...entities,
      ])

      message.value.store.values = [...message.value.store.values, ...values]
      message.value.hasNextPage = hasNextPage
    } finally {
      loading.value = false
    }
  }

  const previousMessagesRequestParameters = computed((): Pagination => {
    const { values, entities } = message.value.store

    if (values.length === 0) {
      return { limit: 0 }
    }

    const until = entities.get(values[0])?.createdAt ?? undefined

    return {
      until,
      limit: NUMBER_OF_MESSAGES_PER_FETCH,
    }
  })

  const fetchPreviousMessages = async (): Promise<void> => {
    if (
      !message.value.hasPrevPage ||
      messages.value.length === 0 ||
      roomId.value == null
    ) {
      return
    }

    loading.value = true

    try {
      const messageUsecase = container.get<MessageUsecase>(TYPES.MessageUsecase)
      const {
        data,
        pageInfo: { hasNextPage: hasPrevPage },
      } = await messageUsecase.getMemberRoomMessages(
        roomId.value,
        entranceGUID,
        previousMessagesRequestParameters.value
      )

      const { values, entities } = normalizeAndFilterMessages(
        message.value.store,
        data
      )

      message.value.store.entities = new Map([
        ...message.value.store.entities,
        ...entities,
      ])

      isDisabledToScrollToBottom.value = true
      message.value.store.values = [...values, ...message.value.store.values]
      message.value.hasPrevPage = hasPrevPage
    } finally {
      loading.value = false
    }
  }

  watch(
    roomId,
    async () => {
      message.value = getInitialMessageState()

      try {
        await fetchNextMessages()
      } catch (error) {
        if (!isClientError(error)) {
          newrelicCaptureException(error)
        }
      }
    },
    { immediate: true }
  )

  return {
    loading,
    messages,
    fetchPreviousMessages,
    fetchNextMessages,
  }
}
